<template>
  <section class="section-block">
    <SectionTitle title="功能列表顯示設定" btn="編輯" @edit="showEditModal = true" />
    <BaseElForm :model="formState" label-position="left" label-width="200px">
      <BaseElFormItem
        v-for="field in visibleFields"
        :key="field.key"
        :label="field.label"
      >
        <BaseElSwitch
          v-if="field.type === 'switch'"
          v-model="formState[field.key]"
          class="switch-input"
          active-text="開啟顯示"
          inactive-text="關閉顯示"
          @change="field.onChange"
        />
        <p v-else-if="field.type === 'text'">{{ field.value }}</p>
      </BaseElFormItem>
    </BaseElForm>
    <MemberCardFuncShowEditModal
      v-if="showEditModal"
      :useExternalTransaction="useExternalTransaction"
      :usePospalTransaction="usePospalTransaction"
      :usePospalBalance="usePospalBalance"
      :useRewardDistribution="useRewardDistribution"
      :useHeadquarterShop="useHeadquarterShop"
      :configData="configData"
      :functionMenuConfig="functionMenuConfig"
      @close="showEditModal = false"
      @refresh="refresh"
    />
  </section>
</template>

<script>
import { defineComponent, ref, reactive, computed, onMounted, watch } from 'vue'
import { find, set as ObjSet, get } from 'lodash'
import SectionTitle from '@/components/Title/SectionTitle.vue'
import { GetSalesConfig, UpdateSalesConfig } from '@/api/sales'
import { UpdateMemberCenterConfig, GetExternalTransactionConfig } from '@/api/member/memberCenter'
import { usePermissions } from '@/use/permissions'
import { formatDate } from '@/utils/date'
import MemberCardFuncShowEditModal from './MemberCardFuncShowEditModal.vue'
import { useShop } from '@/use/shop'
import notifyMessage from '@/config/notifyMessage'

export default defineComponent({
  name: 'MemberCardFuncShow',
  components: {
    SectionTitle,
    MemberCardFuncShowEditModal,
  },
  props: {
    functionMenuConfig: {
      type: Array,
      default: () => [],
    },
  },
  setup (props, { emit }) {
    const showEditModal = ref(false)
    const { checkAction } = usePermissions()

    // Permission checks
    const usePospalTransaction = computed(() => checkAction('adminView.memberCenterConfig.posPalMemberTransaction'))
    const usePospalPassProduct = computed(() => checkAction('adminView.memberCenterConfig.posPalMemberPassProduct'))
    const usePospalBalance = computed(() => checkAction('adminView.memberCenterConfig.findPosPalMemberWalletRecord'))
    const useExternalTransaction = computed(() => checkAction('adminView.memberCenterConfig.memberExternalTransaction'))
    const useSalesRecord = computed(() => checkAction('admin.salesRecord.page'))
    const useRewardDistribution = computed(() => checkAction('admin.rewardDistribution.page'))
    const useHeadquarterShop = computed(() => checkAction('planModule.headquarterShop.enable'))

    // Shop ID from store
    const { shopId } = useShop()
    // Form state
    const formState = reactive({
      showMember: false,
      showMemberDetail: false,
      posPalMemberWallet: false,
      posPalMemberTransaction: false,
      posPalMemberPassProduct: false,
      userInfoMoreAddress: false,
      externalMemberTransaction: false,
      rewardDistribution: false,
      rewardDistributionLinkUseRecord: false,
      memberChannelExpenseData: false,
      orgSalesRecord: false,
      orgSalesRecordDetail: false,
    })

    // External transaction config
    const enableClientDisplayTimeRange = ref(false)
    const clientDisplayTimeRangeStartAt = ref(null)
    const clientDisplayTimeRangeEndAt = ref(null)

    // Config data for modal
    const configData = computed(() => ({
      showMember: formState.showMember,
      showMemberDetail: formState.showMemberDetail,
      posPalMemberWallet: formState.posPalMemberWallet,
      posPalMemberTransaction: formState.posPalMemberTransaction,
      posPalMemberPassProduct: formState.posPalMemberPassProduct,
      userInfoMoreAddress: formState.userInfoMoreAddress,
      externalMemberTransaction: formState.externalMemberTransaction,
      enableClientDisplayTimeRange: enableClientDisplayTimeRange.value,
      clientDisplayTimeRangeStartAt: clientDisplayTimeRangeStartAt.value,
      clientDisplayTimeRangeEndAt: clientDisplayTimeRangeEndAt.value,
      rewardDistribution: formState.rewardDistribution,
      rewardDistributionLinkUseRecord: formState.rewardDistributionLinkUseRecord,
      memberChannelExpenseData: formState.memberChannelExpenseData,
      orgSalesRecord: formState.orgSalesRecord,
      orgSalesRecordDetail: formState.orgSalesRecordDetail,
    }))

    /**
     * Field configuration structure for dynamic rendering
     */
    const fieldConfig = computed(() => [
      {
        key: 'showMember',
        label: '會員顯示銷售消費紀錄',
        type: 'switch',
        visible: () => useSalesRecord.value,
        onChange: () => updateMemberCard('showMember'),
      },
      {
        key: 'showMemberDetail',
        label: '會員顯示銷售消費明細',
        type: 'switch',
        visible: () => formState.showMember,
        onChange: () => updateMemberCard('showMemberDetail'),
      },
      {
        key: 'orgSalesRecord',
        label: '會員顯示組織銷售消費紀錄',
        type: 'switch',
        visible: () => useHeadquarterShop.value,
        onChange: () => updateFunctionMenu(),
      },
      {
        key: 'orgSalesRecordDetail',
        label: '會員顯示組織銷售消費明細',
        type: 'switch',
        visible: () => formState.orgSalesRecord,
        onChange: () => updateFunctionMenu(),
      },
      {
        key: 'userInfoMoreAddress',
        label: '會員顯示常用地址',
        type: 'switch',
        visible: () => true,
        onChange: () => updateFunctionMenu(),
      },
      {
        key: 'posPalMemberTransaction',
        label: '會員顯示瑞乘消費紀錄',
        type: 'switch',
        visible: () => usePospalBalance.value,
        onChange: () => updateFunctionMenu(),
      },
      {
        key: 'posPalMemberWallet',
        label: '會員顯示瑞乘會員餘額紀錄',
        type: 'switch',
        visible: () => usePospalTransaction.value,
        onChange: () => updateFunctionMenu(),
      },
      {
        key: 'posPalMemberPassProduct',
        label: '會員顯示瑞乘會員次卡紀錄',
        type: 'switch',
        visible: () => usePospalPassProduct.value,
        onChange: () => updateFunctionMenu(),
      },
      {
        key: 'externalMemberTransaction',
        label: '會員顯示第三方消費紀錄',
        type: 'switch',
        visible: () => useExternalTransaction.value,
        onChange: () => updateFunctionMenu(),
      },
      {
        key: 'enableClientDisplayTimeRange',
        label: '限定顯示區間',
        type: 'text',
        visible: () => useExternalTransaction.value && formState.externalMemberTransaction,
        value: enableClientDisplayTimeRange.value ? '限定' : '不限定',
      },
      {
        key: 'clientDisplayTimeRangeStartAt',
        label: '開始時間',
        type: 'text',
        visible: () => formState.externalMemberTransaction && enableClientDisplayTimeRange.value,
        value: computed(() => clientDisplayTimeRangeStartAt.value ? formatDate(clientDisplayTimeRangeStartAt.value, 'YYYY/MM/DD HH:mm') : '-'),
      },
      {
        key: 'clientDisplayTimeRangeEndAt',
        label: '結束時間',
        type: 'text',
        visible: () => formState.externalMemberTransaction && enableClientDisplayTimeRange.value,
        value: computed(() => clientDisplayTimeRangeEndAt.value ? formatDate(clientDisplayTimeRangeEndAt.value, 'YYYY/MM/DD HH:mm') : '-目前更新時間'),
      },
      {
        key: 'rewardDistribution',
        label: '會員顯示獎勵發放活動',
        type: 'switch',
        visible: () => useRewardDistribution.value,
        onChange: () => updateFunctionMenu(),
      },
      {
        key: 'rewardDistributionLinkUseRecord',
        label: '會員顯示獎勵發放紀錄',
        type: 'switch',
        visible: () => useRewardDistribution.value,
        onChange: () => updateFunctionMenu(),
      },
      {
        key: 'memberChannelExpenseData',
        label: '會員各管道消費累計',
        type: 'switch',
        visible: () => true,
        onChange: () => updateFunctionMenu(),
      },
    ])

    // Filter visible fields
    const visibleFields = computed(() => fieldConfig.value.filter(field => field.visible()))

    /**
     * Sync data from functionMenuConfig to formState
     */
    const syncData = () => {
      setTimeout(() => {
        const config = props.functionMenuConfig
        const configMap = {
          posPalMemberWallet: 'posPalMemberWallet',
          posPalMemberTransaction: 'posPalMemberTransaction',
          posPalMemberPassProduct: 'posPalMemberPassProduct',
          memberChannelExpenseData: 'memberChannelExpenseData',
          userInfoMoreAddress: 'userInfoMoreAddress',
          memberExternalTransaction: 'externalMemberTransaction',
          rewardDistribution: 'rewardDistribution',
          rewardDistributionLinkUseRecord: 'rewardDistributionLinkUseRecord',
          orgSalesRecord: 'orgSalesRecord',
          orgSalesRecordDetail: 'orgSalesRecordDetail',
        }

        Object.entries(configMap).forEach(([configType, stateKey]) => {
          const configItem = find(config, { type: configType })
          const defaultValue = configType.startsWith('reward')
          formState[stateKey] = get(configItem, 'enable', defaultValue)
        })
      }, 500)
    }

    /**
     * Update function menu configuration
     */
    const updateFunctionMenu = async () => {
      const config = [...props.functionMenuConfig]
      const updates = [
        { type: 'posPalMemberWallet', key: 'posPalMemberWallet' },
        { type: 'posPalMemberPassProduct', key: 'posPalMemberPassProduct' },
        { type: 'memberChannelExpenseData', key: 'memberChannelExpenseData' },
        { type: 'posPalMemberTransaction', key: 'posPalMemberTransaction' },
        { type: 'userInfoMoreAddress', key: 'userInfoMoreAddress' },
        { type: 'memberExternalTransaction', key: 'externalMemberTransaction' },
        { type: 'rewardDistribution', key: 'rewardDistribution' },
        { type: 'rewardDistributionLinkUseRecord', key: 'rewardDistributionLinkUseRecord' },
        { type: 'orgSalesRecord', key: 'orgSalesRecord' },
        { type: 'orgSalesRecordDetail', key: 'orgSalesRecordDetail' },
      ]

      updates.forEach(({ type, key }) => {
        const existingConfig = find(config, { type })
        if (existingConfig) {
          ObjSet(existingConfig, 'enable', formState[key])
        } else {
          config.push({ type, enable: formState[key] })
        }
      })

      const [, err] = await UpdateMemberCenterConfig({
        shopId: shopId.value,
        functionMenuSetting: config,
      })

      if (err) {
        window.$message.error(err)
        return
      }

      window.$message.success(notifyMessage.updateSuccess)
      emit('refresh')
    }

    /**
     * Get sales configuration
     */
    const getSalseConfig = async () => {
      try {
        const res = await GetSalesConfig({ shopId: shopId.value })
        formState.showMember = res.showMember || false
        formState.showMemberDetail = res.showMemberDetail || false
      } catch (error) {
        console.log(error)
        window.$message.error(error?.message || error)
      }
    }

    /**
     * Get external transaction configuration
     */
    const getExternalTransactionConfig = async () => {
      const [res, err] = await GetExternalTransactionConfig({
        shopId: shopId.value,
      })

      if (err) {
        window.$message.error(err)
        return
      }

      enableClientDisplayTimeRange.value = res.enableClientDisplayTimeRange
      clientDisplayTimeRangeStartAt.value = res.clientDisplayTimeRangeStartAt
      clientDisplayTimeRangeEndAt.value = res.clientDisplayTimeRangeEndAt
    }

    /**
     * Update member card configuration
     */
    const updateMemberCard = async (mode) => {
      try {
        await UpdateSalesConfig({
          shopId: shopId.value,
          showMember: mode === 'showMember' ? formState.showMember : undefined,
          showMemberDetail: mode === 'showMemberDetail' ? formState.showMemberDetail : undefined,
        })
        window.$message.success(notifyMessage.updateSuccess)
        await getSalseConfig()
      } catch (error) {
        console.log(error)
        window.$message.error(error?.message || error)
      }
    }

    /**
     * Refresh all configuration data
     */
    const refresh = async (shouldEmit = true) => {
      if (shouldEmit) emit('refresh')
      if (useSalesRecord.value) await getSalseConfig()
      if (useExternalTransaction.value) await getExternalTransactionConfig()
      syncData()
    }

    // Watch for functionMenuConfig changes
    watch(() => props.functionMenuConfig, () => {
      syncData()
    }, { deep: true })

    // Initialize on mount
    onMounted(async () => {
      await refresh(false)
    })

    return {
      showEditModal,
      formState,
      visibleFields,
      configData,
      usePospalTransaction,
      usePospalBalance,
      useExternalTransaction,
      useRewardDistribution,
      useHeadquarterShop,
      refresh,
      formatDate,
    }
  },
})
</script>
